import { useState, useEffect } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, TrendingUp, Globe, Clock, ArrowLeft, FileText } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { useNavigate } from "react-router-dom";

interface VisitorData {
  hour: string;
  count: number;
}

interface CountryData {
  country: string;
  count: number;
}

interface PageData {
  page: string;
  count: number;
  uniqueVisitors: number;
}

export default function Analytics() {
  const { user, isLoading: authLoading } = useAuth();
  const { hasAccess, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [visitorsByHour, setVisitorsByHour] = useState<VisitorData[]>([]);
  const [visitorsByCountry, setVisitorsByCountry] = useState<CountryData[]>([]);
  const [topPages, setTopPages] = useState<PageData[]>([]);
  const [totalVisits, setTotalVisits] = useState(0);
  const [uniqueVisitors, setUniqueVisitors] = useState(0);

  const isAdmin = hasAccess(['admin']);

  useEffect(() => {
    if (isAdmin) {
      fetchAnalytics();
    }
  }, [isAdmin]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);

      // Fetch total visits
      const { count: total } = await supabase
        .from('page_visits')
        .select('*', { count: 'exact', head: true });
      
      setTotalVisits(total || 0);

      // Fetch unique visitors (count distinct user_id where not null, plus distinct IP combinations)
      const { data: allVisits } = await supabase
        .from('page_visits')
        .select('user_id, user_agent');

      if (allVisits) {
        const uniqueIdentifiers = new Set();
        allVisits.forEach((visit) => {
          // Use user_id if available, otherwise use user_agent as identifier
          const identifier = visit.user_id || visit.user_agent;
          if (identifier) {
            uniqueIdentifiers.add(identifier);
          }
        });
        setUniqueVisitors(uniqueIdentifiers.size);
      }

      // Fetch visits from last 7 days for hourly breakdown
      const sevenDaysAgo = subDays(new Date(), 7);
      const { data: recentVisits, error: visitsError } = await supabase
        .from('page_visits')
        .select('created_at')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (!visitsError && recentVisits) {
        // Group by hour
        const hourlyData: { [key: string]: number } = {};
        recentVisits.forEach((visit) => {
          const hour = format(new Date(visit.created_at), 'MMM dd, HH:00');
          hourlyData[hour] = (hourlyData[hour] || 0) + 1;
        });

        const chartData = Object.entries(hourlyData)
          .map(([hour, count]) => ({ hour, count }))
          .slice(-48); // Last 48 hours for better visibility

        setVisitorsByHour(chartData);
      }

      // Fetch country distribution
      const { data: countryData, error: countryError } = await supabase
        .from('page_visits')
        .select('country');

      if (!countryError && countryData) {
        const countryCount: { [key: string]: number } = {};
        countryData.forEach((visit) => {
          const country = visit.country || 'Unknown';
          countryCount[country] = (countryCount[country] || 0) + 1;
        });

        const sortedCountries = Object.entries(countryCount)
          .map(([country, count]) => ({ country, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10); // Top 10 countries

        setVisitorsByCountry(sortedCountries);
      }

      // Fetch top pages with unique visitor counts
      const { data: pageData, error: pageError } = await supabase
        .from('page_visits')
        .select('page_path, user_id, user_agent');

      if (!pageError && pageData) {
        const pageStats: { 
          [key: string]: { 
            count: number; 
            visitors: Set<string>;
          } 
        } = {};
        
        pageData.forEach((visit) => {
          const page = visit.page_path || '/';
          const identifier = visit.user_id || visit.user_agent || 'unknown';
          
          if (!pageStats[page]) {
            pageStats[page] = { count: 0, visitors: new Set() };
          }
          
          pageStats[page].count += 1;
          pageStats[page].visitors.add(identifier);
        });

        const sortedPages = Object.entries(pageStats)
          .map(([page, stats]) => ({ 
            page, 
            count: stats.count,
            uniqueVisitors: stats.visitors.size
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 15); // Top 15 pages

        setTopPages(sortedPages);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading || roleLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!user || !isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto mt-16 text-center space-y-6">
          <h1 className="text-4xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">
            You must be an administrator to access this page.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Visitor Analytics</h1>
              <p className="text-muted-foreground">Track website traffic and visitor patterns</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Admin
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Visitor Trends (Last 48 Hours)
                </CardTitle>
                <CardDescription>Peak traffic hours and activity patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={visitorsByHour}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="hour" 
                      className="text-xs"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis className="text-xs" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Visitors by Country (Top 10)
                </CardTitle>
                <CardDescription>Geographic distribution of your audience</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={visitorsByCountry} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis 
                      type="category" 
                      dataKey="country" 
                      className="text-xs"
                      width={120}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="hsl(var(--primary))"
                      radius={[0, 8, 8, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Top Visited Pages
                </CardTitle>
                <CardDescription>Most popular pages on your site</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {topPages.map((page, index) => {
                    const percentage = (page.count / totalVisits) * 100;
                    const isAdminPage = page.page === '/admin';
                    return (
                      <div 
                        key={page.page} 
                        className={`space-y-2 ${isAdminPage ? 'bg-accent/30 p-3 rounded-lg border border-accent' : ''}`}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium truncate max-w-[300px]" title={page.page}>
                            {index + 1}. {page.page}
                            {isAdminPage && <span className="ml-2 text-xs text-accent-foreground">(Admin Only)</span>}
                          </span>
                          <div className="flex flex-col items-end gap-1 ml-4">
                            <span className="text-muted-foreground">
                              {page.count} visits
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {page.uniqueVisitors} unique visitor{page.uniqueVisitors !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary rounded-full h-2 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle>Total Visits</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">{totalVisits.toLocaleString()}</div>
                  <p className="text-sm text-muted-foreground mt-2">All page views</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Unique Visitors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">{uniqueVisitors.toLocaleString()}</div>
                  <p className="text-sm text-muted-foreground mt-2">Individual users</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Countries Reached</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold">{visitorsByCountry.length}</div>
                  <p className="text-sm text-muted-foreground mt-2">Geographic diversity</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
